// src/config/axiosConfig.js
import axios from 'axios';
import { auth } from './firebaseConfig';
import { BACKEND_URL } from './backendConfig';
import { getWorkspaceHeader } from './workspaceHeader';
import { getSessionId } from './sessionId';
import { reportError } from '../notifications/errorBus';

const apiClient = axios.create({
    baseURL: BACKEND_URL,
});

// Request Interceptor: Attaches Token + active Workspace ID
apiClient.interceptors.request.use(async (config) => {
    const user = auth.currentUser;
    if (user) {
        // Force-refresh on retry (M-F11) — see the response interceptor below.
        const token = await user.getIdToken(config._forceTokenRefresh === true);
        config.headers.Authorization = `Bearer ${token}`;
    }

    const workspaceId = getWorkspaceHeader();
    if (workspaceId) {
        config.headers['x-workspace-id'] = workspaceId;
    }

    // S3: identifies this browser in the user's device list. Carries no
    // authority — see config/sessionId.js. Omitted when storage is unavailable.
    const sessionId = getSessionId();
    if (sessionId) {
        config.headers['x-session-id'] = sessionId;
    }

    return config;
}, (error) => {
    return Promise.reject(error);
});

/**
 * Surfaces an API error in the notification center, unless the caller opted out.
 * Pass `{ skipGlobalErrorToast: true }` in the request config when a component
 * handles the error itself (e.g. inline validation, or its own summary toast),
 * or `{ errorTitle: '...' }` to give the notification a friendly title while
 * still showing the full diagnostic detail.
 */
function reportApiError(error) {
    if (axios.isCancel?.(error)) return;
    const config = error?.config || {};
    if (config.skipGlobalErrorToast) return;
    reportError(error, { source: 'api', title: config.errorTitle });
}

// M-F11: on a 401 (expired/revoked token, clock skew), force-refresh the
// Firebase ID token and retry the request ONCE instead of failing outright.
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const config = error.config;
        const status = error.response?.status;

        if (status === 401 && config && !config._retriedAfter401 && auth.currentUser) {
            config._retriedAfter401 = true;
            config._forceTokenRefresh = true;
            try {
                // The transparent retry — don't report the initial 401.
                return await apiClient.request(config);
            } catch (retryError) {
                // The retried request already passed through this interceptor and
                // was reported there; just propagate without reporting twice.
                return Promise.reject(retryError);
            }
        }

        reportApiError(error);
        return Promise.reject(error);
    }
);

export default apiClient;
